import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  ClientOnboardingState,
  KbDraftJob,
  UpdateOnboardingInput,
} from "@school-sales-ai/shared";
import {
  ApiError,
  api,
  createClientApi,
  type ClientProfileInput,
  type SuperAdminClient,
} from "@/lib/api";
import type { ClientSettings } from "@/hooks/useSettings";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";

/**
 * The onboarding wizard's resumable position.
 *
 * Addressed by `:clientId` in the path, not by the `X-Client-Id` header: this is a
 * super-admin resource ABOUT a client. It stores only where the operator got to —
 * each step's real artifact is written by its own endpoint.
 */

const onboardingKey = (clientId: string) => ["onboarding", clientId] as const;

export function useOnboardingQuery(
  clientId: string | undefined,
): ReturnType<typeof useQuery<ClientOnboardingState>> {
  return useQuery<ClientOnboardingState>({
    queryKey: onboardingKey(clientId ?? ""),
    queryFn: () =>
      api.get<ClientOnboardingState>(`/super-admin/onboarding/${clientId!}`),
    enabled: Boolean(clientId),
  });
}

export function useOnboardingPatchMutation(
  clientId: string | undefined,
): ReturnType<typeof useMutation<ClientOnboardingState, Error, UpdateOnboardingInput>> {
  const queryClient = useQueryClient();

  return useMutation<ClientOnboardingState, Error, UpdateOnboardingInput>({
    mutationFn: (input) =>
      api.patch<ClientOnboardingState>(`/super-admin/onboarding/${clientId!}`, input),
    onSuccess: (state) => {
      // Write the response straight into the cache: the wizard reads the step from
      // here, so a refetch round trip would flash the previous step.
      queryClient.setQueryData(onboardingKey(clientId ?? ""), state);
      // Both Clients lists show the progress badge off the client payload — the
      // super-admin's and the account manager's portfolio.
      void queryClient.invalidateQueries({ queryKey: ["super-admin", "clients"] });
      void queryClient.invalidateQueries({ queryKey: ["my-clients"] });
    },
    onError: (error) => {
      toast.error(error.message ?? "No se pudo guardar el avance del onboarding.");
    },
  });
}

const kbDraftKey = (clientId: string) => ["onboarding", clientId, "kb-draft"] as const;

/** How often the wizard asks whether a running generation has landed. */
const KB_DRAFT_POLL_MS = 4000;

/**
 * The step-4 knowledge base draft job.
 *
 * Generating one takes ~100 s, so the backend runs it detached from the request and stores
 * the result; this only reports where it got to. Polling stops by itself the moment the job
 * is no longer running — 4 s rather than a tighter loop because the API's rate limit is 100
 * requests a minute for the whole CRM, and four seconds of latency on a hundred-second job
 * is not worth a fifth of that budget.
 */
export function useKbDraftQuery(
  clientId: string | undefined,
  enabled: boolean,
): ReturnType<typeof useQuery<KbDraftJob>> {
  return useQuery<KbDraftJob>({
    queryKey: kbDraftKey(clientId ?? ""),
    queryFn: () => api.get<KbDraftJob>(`/super-admin/onboarding/${clientId!}/kb-draft`),
    enabled: Boolean(clientId) && enabled,
    // The draft is durable state, not a cached read: asking again must report what the job
    // is actually doing, or a poll would return the same "running" it started with.
    staleTime: 0,
    refetchInterval: (query) =>
      query.state.data?.status === "running" ? KB_DRAFT_POLL_MS : false,
  });
}

/**
 * Starts a generation. Returns as soon as the job is queued, not when the draft is ready.
 *
 * Writes nothing to the knowledge base: the draft is stored on the onboarding row for the
 * operator to review, and saving stays a separate explicit action through `useKbMutation`.
 * No toast on success — the step shows the state, which is feedback enough.
 */
export function useKbDraftStartMutation(
  clientId: string | undefined,
): ReturnType<typeof useMutation<KbDraftJob, Error, void>> {
  const queryClient = useQueryClient();

  return useMutation<KbDraftJob, Error, void>({
    mutationFn: () =>
      api.post<KbDraftJob>(`/super-admin/onboarding/${clientId!}/kb-draft`, {}),
    onSuccess: (job) => {
      // Seeds the polling query with `running` so the UI switches over without waiting a
      // full interval for the first poll.
      queryClient.setQueryData(kbDraftKey(clientId ?? ""), job);
    },
    onError: (error, _vars, _ctx) => {
      // A 409 means a generation is already running — for the operator that is the state
      // they asked for, not a failure. The poll will show them where it got to.
      if (error instanceof ApiError && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: kbDraftKey(clientId ?? "") });
        return;
      }
      toast.error(error.message ?? "No se pudo generar el borrador.");
    },
  });
}

/**
 * Drops the stored draft once the knowledge base is saved.
 *
 * Housekeeping: step 4 prefers a saved knowledge base over a draft, so a failed clear still
 * leaves the operator looking at the right document. Hence no toast on either outcome.
 */
export function useKbDraftDeleteMutation(
  clientId: string | undefined,
): ReturnType<typeof useMutation<void, Error, void>> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: () => api.delete<void>(`/super-admin/onboarding/${clientId!}/kb-draft`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: kbDraftKey(clientId ?? "") });
    },
  });
}

/**
 * Fetches the segment's empty knowledge base template — the way through step 4 when step 2
 * was skipped and there is no material for the model.
 *
 * A mutation over a GET because it runs on a click and its result goes straight into the
 * editor, never into a cache another component reads. Free and instant; writes nothing.
 */
export function useKbTemplateMutation(
  clientId: string | undefined,
): ReturnType<typeof useMutation<{ content: string }, Error, void>> {
  return useMutation<{ content: string }, Error, void>({
    mutationFn: () =>
      api.get<{ content: string }>(`/super-admin/onboarding/${clientId!}/kb-template`),
    onError: (error) => {
      toast.error(error.message ?? "No se pudo cargar la plantilla.");
    },
  });
}

export type CreateClientForOnboardingInput = ClientProfileInput & {
  slug: string;
  settings: Pick<ClientSettings, "googleMapsUrl" | "primaryColor">;
};

/**
 * Step 1 of a brand new client: create the row, then write the two presentation
 * settings, then record the step.
 *
 * Three calls rather than one because they are three resources, and they must run in
 * this order for a specific reason: `PUT /crm/settings` is scoped by the `X-Client-Id`
 * header, so the switcher has to point at the client that was just created before it
 * is called. Pinning it here (rather than waiting for the wizard's own gate on the
 * next render) is what keeps the settings write from landing on whatever tenant was
 * selected before.
 *
 * `initialOptions` is sent as null because a client that did not exist a moment ago
 * cannot have any — this is the one place where sending null is not a data loss risk.
 */
export function useCreateClientForOnboarding(): ReturnType<
  typeof useMutation<SuperAdminClient, Error, CreateClientForOnboardingInput>
> {
  const queryClient = useQueryClient();

  return useMutation<SuperAdminClient, Error, CreateClientForOnboardingInput>({
    mutationFn: async ({ settings, ...profile }) => {
      // The only irreversible call: the slug is unique, so a retry after this
      // succeeded 409s forever.
      const created = await createClientApi(profile);

      useClientSwitcherStore.getState().setActiveClient(created.id, created.name);

      // Deliberately non-fatal. Both are retryable from step 1 once the wizard is
      // pointed at the new client, and throwing here would strand the operator: the
      // client exists, but the URL still says `new`, so the only button available
      // re-POSTs a slug that is now taken.
      try {
        await api.put<ClientSettings>("/crm/settings", {
          initialOptions: null,
          googleMapsUrl: settings.googleMapsUrl,
          primaryColor: settings.primaryColor,
          greeting: null,
        });

        await api.patch<ClientOnboardingState>(
          `/super-admin/onboarding/${created.id}`,
          { completedStep: 1, currentStep: 2 },
        );
      } catch (err) {
        toast.warning(
          err instanceof Error
            ? `El cliente se creó, pero algo quedó pendiente: ${err.message}`
            : "El cliente se creó, pero no se pudo guardar todo. Revisa el paso 1.",
        );
      }

      return created;
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["super-admin", "clients"] });
      // An account manager who creates a school owns it, so it belongs in their
      // portfolio from this moment — the list they came from must refetch.
      void queryClient.invalidateQueries({ queryKey: ["my-clients"] });
      void queryClient.invalidateQueries({ queryKey: onboardingKey(created.id) });
      // The new client's settings are the ones the next steps will read.
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (error) => {
      // A slug conflict is rendered on the field by step 1; a toast saying the same
      // thing would just be noise the operator has to dismiss.
      if (error instanceof ApiError && error.status === 409) return;
      toast.error(error.message ?? "No se pudo crear el cliente.");
    },
  });
}
